import React from "react";
import { ActionMenuCell } from "./ActionMenuCell";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../../../constants/ZoomAttendance.constants";
import {
  formatMinutesText,
  isParticipantUnrecognized,
} from "../../../utils/ZoomAttendance.utils";

const formatUnrecognizedName = (record) => (
  <span className="tw-font-medium tw-text-gray-900">
    {record.name ||
      record.customer_name ||
      ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_DATA}
  </span>
);

const formatUnrecognizedEmail = (record) =>
  record.user_email ||
  record.customer_email ||
  ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_DATA;

const formatJoinTime = (record) =>
  record.join_time || ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_DATA;

const formatLeaveTime = (record) =>
  record.leave_time || ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_DATA;

const formatDuration = (record) =>
  record.duration !== undefined && record.duration !== null
    ? formatMinutesText(record.duration)
    : formatMinutesText(record.total_minutes_attended);

const formatStatusBadge = (record) => (
  <span className="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-amber-100 tw-text-amber-800">
    {record.status || ZOOM_ATTENDANCE_CONSTANTS.LABELS.UNRECOGNIZED}
  </span>
);

export const getUnrecognizedColumnsConfig = ({
  onReconcileClick,
  onMapToBooking,
  onCreateNewBooking,
}) => [
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.NAME,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.NAME,
    fixed: "left",
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_180PX,
    valueFormatter: formatUnrecognizedName,
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.USER_EMAIL,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.JOINED_WITH_EMAIL,
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_200PX,
    valueFormatter: formatUnrecognizedEmail,
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.JOIN_TIME,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.JOIN_TIME,
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_150PX,
    valueFormatter: formatJoinTime,
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.LEAVE_TIME,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.LEAVE_TIME,
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_150PX,
    valueFormatter: formatLeaveTime,
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.DURATION,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.DURATION,
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_130PX,
    valueFormatter: formatDuration,
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.STATUS,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.STATUS,
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_130PX,
    valueFormatter: formatStatusBadge,
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.ACTION,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.ACTION,
    fixed: "right",
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_120PX,
    sortable: false,
    valueFormatter: (record) => (
      <ActionMenuCell
        record={record}
        onMapToBooking={onMapToBooking || onReconcileClick}
        onCreateNewBooking={onCreateNewBooking}
      />
    ),
  },
];

const formatCustomerName = (record) => (
  <span className="tw-font-medium tw-text-gray-900">
    {record.customer_name ||
      record.name ||
      ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_DATA}
  </span>
);

const formatCustomerEmail = (record) =>
  record.customer_email ||
  record.user_email ||
  ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_DATA;

const formatCustomerPhone = (record) =>
  record.customer_phone ||
  record.customer_phone_numer ||
  ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_DATA;

const formatAttendedBadge = (record) => (
  <span
    className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold ${
      record.has_attended
        ? "tw-bg-emerald-100 tw-text-emerald-800"
        : "tw-bg-rose-100 tw-text-rose-800"
    }`}
  >
    {record.has_attended
      ? ZOOM_ATTENDANCE_CONSTANTS.LABELS.ATTENDED
      : ZOOM_ATTENDANCE_CONSTANTS.LABELS.ABSENT}
  </span>
);

export const getStandardColumnsConfig = ({
  onReconcileClick,
  onViewReportClick,
  onMapToBooking,
  onCreateNewBooking,
}) => [
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.NAME,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.CUSTOMER_NAME,
    fixed: "left",
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_180PX,
    valueFormatter: formatCustomerName,
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.USER_EMAIL,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.EMAIL,
    valueFormatter: formatCustomerEmail,
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.CUSTOMER_PHONE,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.PHONE,
    valueFormatter: formatCustomerPhone,
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.HAS_ATTENDED,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.ATTENDED,
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_130PX,
    valueFormatter: formatAttendedBadge,
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.TOTAL_MINUTES_ATTENDED,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.TOTAL_MINS,
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_130PX,
    valueFormatter: (record) =>
      formatMinutesText(record.total_minutes_attended),
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.TIMES_JOINED,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.TIMES_JOINED,
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_130PX,
    valueFormatter: (record) =>
      record.times_joined ?? ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO,
  },
  {
    field: ZOOM_ATTENDANCE_CONSTANTS.FIELDS.ACTION,
    headerName: ZOOM_ATTENDANCE_CONSTANTS.LABELS.ACTION,
    fixed: "right",
    width: ZOOM_ATTENDANCE_CONSTANTS.WIDTHS.WIDTH_80PX,
    sortable: false,
    valueFormatter: (record) => {
      if (isParticipantUnrecognized(record)) {
        return (
          <ActionMenuCell
            record={record}
            onMapToBooking={onMapToBooking || onReconcileClick}
            onCreateNewBooking={onCreateNewBooking}
          />
        );
      }
      if (!record.has_attended) {
        return (
          <span className="tw-text-gray-400">
            {ZOOM_ATTENDANCE_CONSTANTS.LABELS.NOT_APPLICABLE}
          </span>
        );
      }
      const handleReportClick = () => {
        const handler = onViewReportClick || onReconcileClick;
        if (handler) handler(record);
      };
      return (
        <span
          onClick={handleReportClick}
          className="tw-text-indigo-600 hover:tw-text-indigo-800 tw-font-medium tw-text-xs tw-cursor-pointer hover:tw-underline"
        >
          {ZOOM_ATTENDANCE_CONSTANTS.LABELS.VIEW_REPORT}
        </span>
      );
    },
  },
];
